import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { ChevronDown, Plus } from "lucide-react";
import { useLocation } from "wouter";
import { useBusinessContext } from "@/providers/business-provider";
import { clearBusinessCache } from "@/lib/queryClient";
import { useLoading } from "@/providers/loading-provider";

// Inline initials. Robust against leading/trailing/multiple spaces (filter
// drops empty parts from the split). Two-letter uppercase result.
function computeInitials(name: string): string {
  return name
    .split(" ")
    .filter(Boolean)
    .map((word) => word[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

export function BusinessSwitcher() {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { businesses, activeBusiness, activeBusinessId, isLoading: isLoadingBusinesses, setActiveBusinessId } = useBusinessContext();
  const [, navigate] = useLocation();
  const { navigateTo, startLoading } = useLoading();

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelectBusiness = (business: any) => {
    if (business.id === activeBusinessId) {
      setIsOpen(false);
      return;
    }
    console.log(`BusinessSwitcher: Switching to ${business.name} (ID: ${business.id})`);
    startLoading();
    clearBusinessCache(activeBusinessId);
    setActiveBusinessId(business.id);
    setIsOpen(false);
    navigateTo(`/dashboard?business=${business.id}&t=${Date.now()}`);
  };

  const handleAddNewBusiness = () => {
    startLoading();
    navigateTo("/business/create");
    setIsOpen(false);
  };

  if (isLoadingBusinesses) {
    return (
      <div className="px-4 py-3 border-b border-[hsl(var(--sidebar-border))]">
        <div className="h-10 w-full bg-[hsl(var(--sidebar-accent))]/20 animate-pulse rounded-md"></div>
      </div>
    );
  }

  // Per-business plan label.
  //   no subscription              -> "No Plan"
  //   isTrial                      -> "Free Trial"
  //   planName / plan_name present -> show it
  //   plan_id is NULL / unknown    -> "No Plan"
  function getPlanLabel(sub: any): string {
    if (!sub) return "No Plan";
    if (sub.isTrial || sub.is_trial) return "Free Trial";
    const name = sub.planName || sub.plan_name || sub.plan?.name;
    return name || "No Plan";
  }

  return (
    <div className="px-4 py-3 border-b border-[hsl(var(--sidebar-border))]">
      <div className="relative" ref={dropdownRef}>
        <Button
          variant="secondary"
          role="combobox"
          aria-expanded={isOpen}
          className="w-full justify-between border-muted"
          onClick={() => setIsOpen(!isOpen)}
        >
          {activeBusiness ? (() => {
            const name = activeBusiness?.name ?? "Unnamed Business";
            const initials = computeInitials(name);
            return (
            <div className="flex items-center flex-grow min-w-0">
              <div className="w-7 h-7 md:w-6 md:h-6 bg-primary/10 rounded-full flex items-center justify-center text-primary text-xs font-bold shrink-0">
                {initials}
              </div>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="ml-2 truncate text-base md:text-sm text-foreground">{name}</span>
                </TooltipTrigger>
                <TooltipContent side="bottom">{name}</TooltipContent>
              </Tooltip>
              <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-primary/10 text-primary hidden md:inline-block">
                {getPlanLabel((activeBusiness as any)?.subscription)}
              </span>
            </div>
            );
          })() : (
            <span className="text-base md:text-sm text-foreground">Select a Business</span>
          )}
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50 text-foreground" />
        </Button>

        {isOpen && (
          <div
            className="business-dropdown absolute z-50 mt-1 w-full shadow-lg rounded-md border max-h-[70vh] overflow-auto"
            style={{
              backgroundColor: '#ffffff !important',
              color: '#000000 !important',
              borderColor: '#e5e7eb',
              boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
            }}
          >
            <div className="p-2">
              {businesses.length > 0 ? (
                businesses.map((business: any) => {
                  // Single source of truth for the displayed name.
                  const name = business?.name ?? "Unnamed Business";
                  const initials = computeInitials(name);
                  // TEMP DEBUG (remove after verification): log the raw shape
                  // of every business the switcher renders.
                  console.log("SWITCHER BUSINESS:", business);
                  // Use THIS business's subscription, not the active one.
                  const label = getPlanLabel(business.subscription);
                  return (
                    <Button
                      key={business.id}
                      variant="ghost"
                      className="w-full justify-start px-3 py-3 md:py-2 text-base md:text-sm"
                      style={{ backgroundColor: 'transparent', color: '#000000' }}
                      onClick={() => handleSelectBusiness(business)}
                    >
                      <div className="flex items-center justify-between w-full min-w-0">
                        <div className="flex items-center min-w-0 flex-grow">
                          <div className="w-8 h-8 md:w-6 md:h-6 bg-primary/10 rounded-full flex items-center justify-center text-primary text-xs font-bold shrink-0">
                            {initials}
                          </div>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="ml-2 line-clamp-2 break-words text-left text-foreground">{name}</span>
                            </TooltipTrigger>
                            <TooltipContent side="right">{name}</TooltipContent>
                          </Tooltip>
                        </div>
                        <span
                          className={
                            label === "No Plan"
                              ? "ml-2 px-2.5 py-1 md:py-0.5 text-xs rounded-full bg-muted text-muted-foreground shrink-0"
                              : "ml-2 px-2.5 py-1 md:py-0.5 text-xs rounded-full bg-primary/10 text-primary shrink-0"
                          }
                        >
                          {label}
                        </span>
                      </div>
                    </Button>
                  );
                })
              ) : (
                <div className="px-3 py-4 text-sm text-muted-foreground text-center">
                  No businesses found
                </div>
              )}
            </div>
            <div className="border-t border-border p-2">
              <Button
                variant="ghost"
                className="w-full justify-start text-base md:text-sm py-3 md:py-2"
                style={{ backgroundColor: 'transparent', color: '#000000' }}
                onClick={handleAddNewBusiness}
              >
                <Plus className="h-5 w-5 md:h-4 md:w-4 mr-2" />
                Add new business
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
